import * as React from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/cn';

interface FabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  to?: string;
  icon?: React.ElementType;
  label?: string;
}

/**
 * Floating Action Button: pinned to the bottom-right of the viewport.
 * Translucent (glass) so content underneath stays visible.
 * Pages must reserve space using `pb-fab` so the last row clears it on scroll.
 */
export const Fab = React.forwardRef<HTMLButtonElement, FabProps>(
  ({ to, icon: Icon = Plus, label, className, children, ...rest }, ref) => {
    const cls = cn(
      'fixed z-40 flex h-14 w-14 items-center justify-center rounded-full text-white transition-transform active:scale-95',
      // Translucent brand background with blur — visible content can show through subtly
      'bg-gradient-to-br from-primary/85 to-accent/85 backdrop-blur-md shadow-soft-lg',
      'ring-1 ring-white/30',
      className,
    );
    const style: React.CSSProperties = {
      // 16px from the right edge, lifted above bottom tab bar with safe-area inset
      right: 'max(1rem, env(safe-area-inset-right, 0px))',
      bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)',
    };
    const inner = (
      <>
        <Icon className="h-6 w-6 drop-shadow-sm" strokeWidth={2.6} />
        {children}
      </>
    );

    if (to) {
      return (
        <Link to={to} aria-label={label ?? 'Действие'} className={cls} style={style}>
          {inner}
        </Link>
      );
    }
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label ?? 'Действие'}
        className={cls}
        style={style}
        {...rest}
      >
        {inner}
      </button>
    );
  },
);
Fab.displayName = 'Fab';
